// Inline source keys written by the model: `[S#]` for web pages, `[D#]` for
// passages of company documents. Both families are disjoint by prefix.

// A run of markers such as ` [S1], [D2]` or `[S1][D3]`, with the spacing before it.
const CITATION_MARKER_GROUP_PATTERN = /[ \t]*\[[SD][1-9]\d*\](?:[ \t]*[,;]?[ \t]*\[[SD][1-9]\d*\])*/g;
const CITATION_KEY_IN_GROUP_PATTERN = /\[([SD][1-9]\d*)\]/g;

export const DOCUMENT_CITATION_KEY_PATTERN = /^D[1-9]\d*$/;

export type StrippedCitationMarkers = {
  documentKeys: string[];
  text: string;
  webKeys: string[];
};

/**
 * Removes citation markers (and the spacing before them) from plain text and
 * returns the known keys in order of appearance.
 * - Unknown web keys stay as ordinary text (historical web behaviour).
 * - Document keys are turn-local internal references: every `[D#]` marker is
 *   removed, and only keys with a known citation produce a badge. The backend
 *   ignores unknown keys instead of rewriting already streamed text.
 */
export function stripCitationMarkers(
  text: string,
  knownWebKeys: ReadonlySet<string>,
  knownDocumentKeys: ReadonlySet<string>,
): StrippedCitationMarkers {
  const webKeys: string[] = [];
  const documentKeys: string[] = [];
  const stripped = text.replace(CITATION_MARKER_GROUP_PATTERN, (group: string) => {
    const unknownWebMarkers: string[] = [];
    for (const [marker, key] of group.matchAll(CITATION_KEY_IN_GROUP_PATTERN)) {
      if (key.startsWith('D')) {
        if (knownDocumentKeys.has(key) && !documentKeys.includes(key)) documentKeys.push(key);
      } else if (!knownWebKeys.has(key)) {
        unknownWebMarkers.push(marker);
      } else if (!webKeys.includes(key)) {
        webKeys.push(key);
      }
    }
    if (unknownWebMarkers.length === 0) {
      return '';
    }
    const leadingSpace = /^[ \t]*/.exec(group)?.[0] ?? '';
    return `${leadingSpace}${unknownWebMarkers.join(', ')}`;
  });
  return { documentKeys, text: stripped, webKeys };
}
